import React, { useEffect, useState, useRef } from 'react';
import socketService from '../services/socketService';
import chatService from '../services/chatService';
import { useChat } from '../context/ChatContext';
import ProfilePicture from './ProfilePicture';
import useIsMobile from '../hooks/useIsMobile';

//ChatModal is a component that displays the chat modal, which is a list of all the messages in the chat
function ChatModal({ room, currentUser, onClose }) {  //room is the chat room, currentUser is the user, onClose is the function to close the chat modal
  const { addOrUpdateRoom } = useChat();
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState([]); //messages is the list of messages in the chat
  const [input, setInput] = useState(''); //input is the input field for the message
  const bottomRef = useRef(null); //bottomRef is the ref to the bottom of the chat
  const messageIdsRef = useRef(new Set()); //messageIdsRef is the ref to the set of message ids

  const otherUser = room.participants.find(p => p._id !== currentUser._id);

  // Load history, this is the history of the chat
  useEffect(() => {
    (async () => {
      const msgs = await chatService.fetchMessages(room._id); //fetchMessages is the function to fetch the messages from the chat
      msgs.forEach(m=>messageIdsRef.current.add(m._id)); //add the message id to the set
      setMessages(msgs); //set the messages to the state
    })();
    socketService.joinRoom(room._id); //join the room
    const recv = (msg) => {
      if (msg.roomId === room._id) addMessage(msg); //add the message to the state
    };
    socketService.onMessage(recv); //onMessage is the function to listen for messages
    return () => {
      socketService.leaveRoom(room._id); //leave the room
      socketService.offMessage(recv); //offMessage is the function to stop listening for messages
    };
  }, [room._id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); //scroll to the bottom of the chat
  }, [messages]);

  const addMessage = (m)=>{
    if (messageIdsRef.current.has(m._id)) return; //if the message id is already in the set, return
    messageIdsRef.current.add(m._id); //add the message id to the set
    setMessages(prev=>[...prev, m]); //set the messages to the state
  };

  const send = async () => {
    const text = input.trim(); //trim the input
    if (!text) return; //if the input is empty, return
    setInput(''); //set the input to the state
    const msg = await chatService.sendMessage(room._id, text);
    addMessage(msg);
    // update drawer list with lastMessage
    addOrUpdateRoom({ ...room, lastMessage: msg.text, updatedAt: msg.createdAt });
  };

  return (
    <div style={{
      position: 'fixed', inset:0,
      background:'rgba(32,32,32,0.45)', zIndex:3000, display:'flex', justifyContent:'center', alignItems:'center',
      fontFamily:'var(--font)'
    }} onClick={onClose}>
      <div style={{ width: isMobile ? '100%' : '400px', height: isMobile ? '100%' : undefined, maxWidth: isMobile ? '100%' : '400px', maxHeight: isMobile ? '100%' : '80vh', background:'var(--surface-card)', border: isMobile ? 'none' : '1px solid var(--border-subtle)', borderRadius: isMobile ? 0 : 'var(--radius-lg, 16px)', boxShadow:'var(--shadow-xl)', display:'flex', flexDirection:'column', margin:'auto', overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
        <div style={{padding:'0.875rem 1rem', background:'var(--surface-bg)', color:'var(--maroon)', borderBottom:'1px solid var(--border-subtle)', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.625rem'}}>
            <ProfilePicture 
              src={otherUser?.profilePicture}
              alt={otherUser?.displayName || 'User'}
              size="30px"
              fallbackColor="var(--maroon)"
            />
            <span style={{fontWeight:600, fontSize:'0.95rem', fontFamily:'var(--font-heading)', color:'var(--maroon)'}}>Chat with {otherUser?.displayName || 'Seller'}</span>
          </div>
          <button onClick={onClose} style={{background:'transparent', color:'var(--text-muted)', border:'1px solid var(--border-subtle)', borderRadius:'50%', width: isMobile ? 40 : 28, height: isMobile ? 40 : 28, fontSize: isMobile ? '1.4rem' : '1.1rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1, flexShrink:0}}>×</button>
        </div>
        <div style={{flex:1, overflowY:'auto', padding:'1rem'}}>
          {messages.map((m,idx)=>{
            const prev=messages[idx-1];
            const curDate=new Date(m.createdAt);
            const prevDate=prev? new Date(prev.createdAt):null;
            const dayChanged=!prevDate|| curDate.toDateString()!==prevDate.toDateString();
            const isCurrentUser = m.senderId._id === currentUser._id;
            
            return (
              <React.Fragment key={m._id}>
                {dayChanged && (
                  <div style={{textAlign:'center', margin:'0.5rem 0', fontSize:'0.75rem', color:'var(--text-soft)'}}>
                    {curDate.toLocaleDateString(undefined,{month:'short', day:'numeric', year:'numeric'})}
                  </div>) }
                <div style={{
                  display: 'flex', 
                  flexDirection: isCurrentUser ? 'row-reverse' : 'row',
                  alignItems: 'flex-end',
                  gap: '8px',
                  marginBottom:'0.5rem'
                }}>
                  <ProfilePicture 
                    src={isCurrentUser ? currentUser.profilePicture : otherUser?.profilePicture}
                    alt={isCurrentUser ? currentUser.displayName : otherUser?.displayName}
                    size="24px"
                    fallbackColor={isCurrentUser ? "var(--maroon)" : "var(--text-soft)"}
                  />
                  <div style={{maxWidth: '70%'}}>
                    <div style={{
                      padding:'0.5rem 0.75rem', 
                      borderRadius:'14px', 
                      background: isCurrentUser ? 'var(--maroon)' : 'var(--surface-panel)', 
                      color: isCurrentUser ? '#fff' : 'var(--text-strong)', 
                      fontSize:'0.9rem',
                      lineHeight:1.45,
                      wordBreak:'break-word'
                    }}>
                      {m.text}
                    </div>
                    <div style={{
                      fontSize:'0.65rem', 
                      color:'var(--text-soft)', 
                      marginTop:'2px',
                      textAlign: isCurrentUser ? 'right' : 'left'
                    }}>
                      {curDate.toLocaleTimeString(undefined,{hour:'2-digit', minute:'2-digit'})}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          <div ref={bottomRef}/>
        </div>
        <div style={{padding:'0.625rem', borderTop:'1px solid var(--border-subtle)', display:'flex', gap:'0.5rem'}}>
          <input 
            value={input}
            onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>{ if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); send(); } }}
            onFocus={e=>{ e.currentTarget.style.borderColor='var(--maroon)'; e.currentTarget.style.boxShadow='0 0 0 3px rgba(136,28,28,0.1)'; }}
            onBlur={e=>{ e.currentTarget.style.borderColor='var(--border-subtle)'; e.currentTarget.style.boxShadow='none'; }}
            style={{flex:1, padding:'0.6rem 0.75rem', borderRadius:'8px', border:'1px solid var(--border-subtle)', fontFamily:'var(--font)', fontSize:'0.9rem', outline:'none', transition:'border-color 0.2s ease, box-shadow 0.2s ease'}} 
            placeholder="Type a message..." />
          <button
            onClick={send}
            style={{background:'var(--maroon)', color:'#fff', border:'none', padding:'0.5rem 1.1rem', borderRadius:'10px', cursor:'pointer', fontFamily:'var(--font-heading)', fontWeight:600, transition:'background 0.2s ease'}}
            onMouseOver={e=>{e.currentTarget.style.background='var(--maroon-hover)';}}
            onMouseOut={e=>{e.currentTarget.style.background='var(--maroon)';}}
          >Send</button>
        </div>
      </div>
    </div>
  );
}

export default ChatModal; 